"use strict";
// from https://github.com/LedgerHQ/xpub-scan/blob/master/src/actions/deriveAddresses.ts
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.fallbackValidateAddress = exports.OP_RETURN_DATA_SIZE_LIMIT = void 0;
const bjs = __importStar(require("bitcoinjs-lib"));
const address_1 = require("bitcoinjs-lib/src/address");
const bs58check_1 = __importDefault(require("bs58check"));
const types_1 = require("../types");
const bs58_1 = __importDefault(require("bs58"));
const bech32_1 = __importDefault(require("bech32"));
const bip32_1 = __importDefault(require("./bip32"));
// https://developer.bitcoin.org/devguide/transactions.html#null-data
exports.OP_RETURN_DATA_SIZE_LIMIT = 83; // bytes
function fallbackValidateAddress(address) {
    try {
        bjs.address.fromBase58Check(address);
    }
    catch (_a) {
        // Not a valid Base58 address
        try {
            bjs.address.fromBech32(address);
        }
        catch (_b) {
            // Not a valid Bech32 address either
            return false;
        }
    }
    return true;
}
exports.fallbackValidateAddress = fallbackValidateAddress;
class Base {
    constructor({ network }) {
        this.network = network;
        this.network.dustThreshold = 3000;
        this.network.dustPolicy = "PER_KBYTE";
        this.network.usesTimestampedTransaction = false;
    }
    getPubkeyAt(xpub, account, index) {
        return __awaiter(this, void 0, void 0, function* () {
            // a cache is stored in Base.bip32Cache to optimize the calculation
            // at each step, we make sure the level has been calculated and calc if necessary
            // 0: root level
            const keyRoot = `${this.network.name}-${xpub}`;
            let rootLevel = Base.bip32Cache[keyRoot]; // it's stored "in sync"
            if (!rootLevel) {
                const buffer = bs58_1.default.decode(xpub);
                const depth = buffer[4];
                const i = buffer.readUInt32BE(9);
                const chainCode = buffer.slice(13, 45);
                const publicKey = buffer.slice(45, 78);
                Base.bip32Cache[keyRoot] = rootLevel = new bip32_1.default(publicKey, chainCode, this.network, depth, i);
            }
            // 1: account level
            const keyAccount = `${keyRoot}-${account}`;
            let accountLevelP = Base.bip32Cache[keyAccount]; // it's stored as promise
            if (!accountLevelP) {
                Base.bip32Cache[keyAccount] = accountLevelP = rootLevel.derive(account);
            }
            // 2: index level
            const keyIndex = `${keyAccount}-${index}`;
            let indexLevelP = Base.bip32Cache[keyIndex]; // it's stored as promise
            if (!indexLevelP) {
                Base.bip32Cache[keyIndex] = indexLevelP = accountLevelP.then(a => a.derive(index));
            }
            // We can finally return the publicKey. in most case, indexLevelP will be "resolved"
            return (yield indexLevelP).publicKey;
        });
    }
    // derive legacy address at account and index positions
    getLegacyAddress(xpub, account, index) {
        return __awaiter(this, void 0, void 0, function* () {
            const publicKeyBuffer = yield this.getPubkeyAt(xpub, account, index);
            const publicKeyHash160 = bjs.crypto.hash160(publicKeyBuffer);
            return bjs.address.toBase58Check(publicKeyHash160, this.network.pubKeyHash);
        });
    }
    // derive native SegWit at account and index positions
    getNativeSegWitAddress(xpub, account, index) {
        return __awaiter(this, void 0, void 0, function* () {
            const publicKeyBuffer = yield this.getPubkeyAt(xpub, account, index);
            const publicKeyHash160 = bjs.crypto.hash160(publicKeyBuffer);
            const words = bech32_1.default.toWords(publicKeyHash160);
            words.unshift(0x00);
            return bech32_1.default.encode(this.network.bech32, words);
        });
    }
    // derive SegWit at account and index positions
    getSegWitAddress(xpub, account, index) {
        return __awaiter(this, void 0, void 0, function* () {
            const publicKeyBuffer = yield this.getPubkeyAt(xpub, account, index);
            const redeemOutput = bjs.script.compile([0, bjs.crypto.hash160(publicKeyBuffer)]);
            const publicKeyHash160 = bjs.crypto.hash160(redeemOutput);
            const payload = Buffer.allocUnsafe(21);
            payload.writeUInt8(this.network.scriptHash, 0);
            publicKeyHash160.copy(payload, 1);
            return bs58check_1.default.encode(payload);
        });
    }
    // get address given an address type
    getAddress(derivationMode, xpub, account, index) {
        return __awaiter(this, void 0, void 0, function* () {
            if (Base.addressCache[`${this.network.name}-${derivationMode}-${xpub}-${account}-${index}`]) {
                return Base.addressCache[`${this.network.name}-${derivationMode}-${xpub}-${account}-${index}`];
            }
            const res = this.customGetAddress(derivationMode, xpub, account, index);
            Base.addressCache[`${this.network.name}-${derivationMode}-${xpub}-${account}-${index}`] = res;
            return res;
        });
    }
    customGetAddress(derivationMode, xpub, account, index) {
        return __awaiter(this, void 0, void 0, function* () {
            switch (derivationMode) {
                case types_1.DerivationModes.LEGACY:
                    return yield this.getLegacyAddress(xpub, account, index);
                case types_1.DerivationModes.SEGWIT:
                    return yield this.getSegWitAddress(xpub, account, index);
                case types_1.DerivationModes.NATIVE_SEGWIT:
                    return yield this.getNativeSegWitAddress(xpub, account, index);
                default:
                    throw new Error(`Invalid derivation Mode: ${derivationMode}`);
            }
        });
    }
    toOutputScript(address) {
        return (0, address_1.toOutputScript)(address, this.network);
    }
    toOpReturnOutputScript(data) {
        const script = bjs.payments.embed({ data: [data] });
        return script.output;
    }
    validateAddress(address) {
        // bs58 address
        const res = bs58check_1.default.decodeUnsafe(address);
        if (!res)
            return false;
        return (res.length > 3 && (res[0] === this.network.pubKeyHash || res[0] === this.network.scriptHash));
    }
    isTaprootAddress(_address) {
        return false;
    }
}
Base.bip32Cache = {}; // xpub + account + index to publicKey
Base.addressCache = {}; // derivationMode + xpub + account + index to address
exports.default = Base;
//# sourceMappingURL=base.js.map